import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ResultBadge, SimBadge } from "./ResultBadge";
import { RefreshCw, Zap, AlertOctagon, Radio } from "lucide-react";

export function AdapterCard({ adapter, onSync, onFail, busy, compact }) {
  const t = adapter?.totals || {};
  const tone = adapter?.status === "error" ? "border-rose-300 bg-rose-50/40" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-5 ${tone}`} data-testid="autofetch-adapter-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Radio size={16} className={adapter?.status === "error" ? "text-rose-600" : "text-emerald-600"} />
            <span className="font-heading font-bold text-slate-900">Adfluence Auto Fetch · FR-02</span>
            <SimBadge label={adapter?.mode === "live" ? "LIVE" : "DEMO · SIMULATED"} />
            <span className="text-xs text-slate-500 capitalize" data-testid="autofetch-status">{(adapter?.status || "idle").replace(/_/g, " ")}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">Last sync {adapter?.last_sync ? new Date(adapter.last_sync).toLocaleString() : "never"}{adapter?.last_error && <span className="text-rose-600 ml-2">· {adapter.last_error}</span>}</div>
        </div>
        <div className="flex gap-2">
          <Button onClick={onSync} disabled={busy} className="bg-[#0052FF] hover:bg-[#0043d1]" data-testid="autofetch-sync-btn"><RefreshCw size={15} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> Sync now</Button>
          <Button variant="outline" onClick={onFail} disabled={busy} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="autofetch-fail-btn"><AlertOctagon size={15} className="mr-1" /> Simulate API failure</Button>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-4" data-testid="autofetch-metrics">
        {[["Runs", t.runs], ["Received", t.received], ["Accepted", t.accepted], ["Rejected", t.rejected], ["Retries", t.retries], ["Errors", t.errors]].map(([l, v]) => (
          <div key={l}><div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div><div className="font-mono text-lg font-bold text-slate-900">{v ?? 0}</div></div>
        ))}
      </div>
      {!compact && adapter?.contract && (
        <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 leading-relaxed">
          <div className="font-semibold text-slate-700 mb-1">Interface contract (placeholder)</div>
          <div><span className="font-mono">query:</span> {adapter.contract.query}</div>
          <div><span className="font-mono">returns:</span> {adapter.contract.returns}</div>
          <div className="text-amber-700 mt-1">{adapter.contract.note} · Live mode requires Adfluence API credentials (production dependency).</div>
        </div>
      )}
    </div>
  );
}

export function AutoFetchPanel({ cid, onChange }) {
  const [adapter, setAdapter] = useState(null);
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [view, setView] = useState("normalized");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, r] = await Promise.all([api.get("/autofetch/status"), api.get(`/autofetch/runs?campaign_id=${cid}`)]);
    setAdapter(a.data); setRuns(r.data);
  };
  useEffect(() => { load(); }, [cid]); // eslint-disable-line

  const sync = async (fail) => {
    setBusy(true);
    try {
      const r = await api.post("/autofetch/sync", fail ? { simulate_failure: true } : { campaign_id: cid });
      setRun(r.data);
      if (r.data.status === "error") toast.error(r.data.error); else toast.success(`Fetched ${r.data.received} records · ${r.data.accepted} accepted · ${r.data.rejected} rejected`);
      await load(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); } finally { setBusy(false); }
  };
  const openRun = async (id) => { const r = await api.get(`/autofetch/runs/${id}`); setRun(r.data); };

  return (
    <div className="space-y-4" data-testid="autofetch-panel">
      <AdapterCard adapter={adapter} busy={busy} onSync={() => sync(false)} onFail={() => sync(true)} compact />
      {runs.length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs" data-testid="autofetch-runs">
          {runs.slice(0, 6).map((r) => (
            <button key={r.id} onClick={() => openRun(r.id)} className={`rounded-full border px-3 py-1 font-mono ${run?.id === r.id ? "bg-slate-900 text-white border-slate-900" : r.status === "error" ? "border-rose-300 text-rose-700 bg-rose-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`autofetch-run-${r.id}`}>
              {new Date(r.started_at).toLocaleTimeString()} · {r.status === "error" ? "ERROR" : `${r.accepted}/${r.received}`}
            </button>
          ))}
        </div>
      )}
      {run && run.status === "error" && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800" data-testid="autofetch-error">{run.error} · retry #{run.retry_count}. Next Sync will recover.</div>}
      {run && run.status === "success" && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="autofetch-records">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-800"><Zap size={14} className="inline mr-1 text-amber-500" />Run {run.id.slice(0, 8)} · {run.received} received · {run.accepted} accepted · {run.rejected} rejected</span>
            <div className="flex gap-1 text-xs">{["normalized", "raw"].map((v) => <button key={v} onClick={() => setView(v)} data-testid={`autofetch-view-${v}`} className={`rounded-full px-3 py-1 font-semibold ${view === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{v}</button>)}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2">Creator</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Reason</th><th className="px-4 py-2">{view} payload</th></tr></thead>
            <tbody>
              {run.records.map((rec, i) => (
                <tr key={i} className="border-t border-slate-100 align-top" data-testid={`autofetch-record-${rec.status}`}>
                  <td className="px-4 py-2">{rec.creator}</td>
                  <td className="px-4 py-2"><ResultBadge value={rec.status === "accepted" ? "PASS" : "FAIL"} /></td>
                  <td className="px-4 py-2 text-xs text-slate-600 max-w-[220px]">{rec.reason}</td>
                  <td className="px-4 py-2"><pre className="text-[10px] font-mono bg-slate-900 text-slate-100 rounded p-2 max-h-28 overflow-auto max-w-[460px]">{JSON.stringify(rec[view], null, 1)}</pre></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
